import { useParams } from 'react-router-dom';
import Button from 'src/components/Button';
import { Tooltip } from 'src/components/Tooltip';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import { useStudentHasServiceType } from 'src/hooks/useStudentHasServiceType';
import { IconChevronLeft } from 'src/icons';
import { routes } from 'src/routes';

import { useSingleStudentDetailsQuery } from '../single-student/singleStudentApi';
import StudentHeaderLoading from '../single-student/student-header/StudentHeaderLoading';
import StudentHeaderParameters from '../single-student/student-header/StudentHeaderParameters';
import StudentHeaderProfile from '../single-student/student-header/StudentHeaderProfile';
import { useInvoiceDetailsQuery } from './billingApi';
import { BillingProcessSteps } from './BillingProcessSteps';
import { getFinalizeTooltipMessage } from './getFinalizeTooltipMessage';

export type BillingHeaderProps = {
  onFinalize: () => void;
};

export const BillingHeader = ({ onFinalize }: BillingHeaderProps) => {
  const { id } = useParams();
  const { isSm } = useBreakpoint('sm');
  const { is2xl } = useBreakpoint('2xl');
  const { data: invoiceData } = useInvoiceDetailsQuery(id!, {
    skip: !id,
  });

  const { data: student, isFetching: isFetchingStudent } = useSingleStudentDetailsQuery(
    invoiceData?.studentId || '',
    {
      skip: !invoiceData?.studentId,
    },
  );

  const hasSeItService = useStudentHasServiceType(invoiceData?.studentId || '', 'SEIT');

  if (!student) return <StudentHeaderLoading />;

  return (
    <div className="h-auto w-full px-4 pb-6 sm:px-10 sm:pb-10 sm:pt-10">
      <Button
        preset="ghost"
        Icon={IconChevronLeft}
        to={`${routes.billing.root}`}
        className="mb-6 inline-flex"
      >
        Back to Invoices
      </Button>
      {!student || isFetchingStudent ? (
        <StudentHeaderLoading />
      ) : (
        <>
          <div className="flex flex-col xl:flex-row">
            <StudentHeaderProfile size="md" student={student} className="mb-4 mr-6 xl:mb-0" />
            <StudentHeaderParameters size="md" student={student} />
          </div>
        </>
      )}
      <div
        className={`mt-4 flex w-full flex-col gap-4 ${
          hasSeItService
            ? '2xl:absolute 2xl:right-10 2xl:top-24 2xl:w-fit 2xl:flex-row 2xl:items-center 2xl:justify-end'
            : 'sm:flex-row sm:items-center sm:justify-end'
        }`}
      >
        {hasSeItService && <BillingProcessSteps />}
        <Tooltip
          content={getFinalizeTooltipMessage(invoiceData)}
          delayDuration={0}
          className="max-w-xs"
          side={is2xl ? 'left' : 'top'}
        >
          <Button
            className={`w-full sm:w-auto ${
              !hasSeItService ? 'sm:absolute sm:right-10 sm:top-24 sm:w-fit' : ''
            }`}
            size={isSm ? 'md' : 'xs'}
            disabled={!invoiceData?.canBeSigned}
            preset="tertiary"
            onClick={onFinalize}
          >
            Finalize invoice
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
